/**
 * What the account home reads: the customer's current formula and every past version, with the
 * ingredient-level diff between consecutive versions (ACC-001, ACC-002). Shared by the Worker that
 * builds the answer and the web app that displays it.
 *
 * Type aliases rather than interfaces so the Worker's `json()` accepts the payload: it takes a JSON
 * value, and only an alias is assignable to an index-signature type.
 */
import { z } from "zod";

export type FormulaFlavor = "vanilla" | "chocolate";

export type FormulaOrigin =
  | "engine"
  | "physician"
  | "customer_removal"
  | "practitioner"
  | "preference";

export type AccountFormulaStatus = "draft" | "in_review" | "approved" | "declined" | "superseded";

export type FormulaChangeKind = "added" | "removed" | "dose_changed";

export type AccountIngredient = {
  readonly ingredientId: string;
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
  readonly reason: string;
  readonly addon: boolean;
};

export type AccountFormula = {
  readonly id: string;
  readonly version: number;
  readonly status: AccountFormulaStatus;
  readonly origin: FormulaOrigin;
  readonly proteinBase: string | null;
  readonly flavor: FormulaFlavor | null;
  readonly physicianName: string | null;
  readonly approvedAt: string | null;
  readonly createdAt: string;
  readonly ingredients: readonly AccountIngredient[];
};

export type FormulaChange = {
  readonly ingredientId: string;
  readonly name: string;
  readonly kind: FormulaChangeKind;
  readonly fromDose: number | null;
  readonly toDose: number | null;
  readonly unit: string;
};

export type AccountFormulaVersion = {
  readonly id: string;
  readonly version: number;
  readonly status: AccountFormulaStatus;
  readonly origin: FormulaOrigin;
  readonly why: string;
  readonly createdAt: string;
  readonly proteinBase: string | null;
  readonly flavor: FormulaFlavor | null;
  readonly changes: readonly FormulaChange[];
};

export type AccountFormulas = {
  readonly current: AccountFormula | null;
  readonly versions: readonly AccountFormulaVersion[];
};

type DiffLine = {
  readonly ingredientId: string;
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
};

/** Ingredient adds, removals and dose edits from one version to the next. */
export function formulaChanges(
  previous: readonly DiffLine[],
  next: readonly DiffLine[],
): FormulaChange[] {
  const before = new Map(previous.map((line) => [line.ingredientId, line]));
  const after = new Map(next.map((line) => [line.ingredientId, line]));
  const changes: FormulaChange[] = [];

  for (const line of next) {
    const was = before.get(line.ingredientId);

    if (was === undefined) {
      changes.push(changeOf(line, "added", null, line.dose));
      continue;
    }

    if (was.dose !== line.dose) changes.push(changeOf(line, "dose_changed", was.dose, line.dose));
  }

  for (const line of previous) {
    if (!after.has(line.ingredientId)) changes.push(changeOf(line, "removed", line.dose, null));
  }

  return changes;
}

function changeOf(
  line: DiffLine,
  kind: FormulaChangeKind,
  fromDose: number | null,
  toDose: number | null,
): FormulaChange {
  return {
    ingredientId: line.ingredientId,
    name: line.name,
    kind,
    fromDose,
    toDose,
    unit: line.unit,
  };
}

/**
 * Why this version exists, in the customer's words. Internal origin names never appear (ACC-002).
 */
export function originWhy(origin: FormulaOrigin, version: number): string {
  switch (origin) {
    case "engine":
      return version === 1 ? "First formula from your labs" : "New labs";
    case "physician":
      return "Physician edit";
    case "customer_removal":
      return "Ingredient removed";
    case "practitioner":
      return "Clinic edit";
    case "preference":
      return "Flavor or protein change";
  }
}

const StoredRationale = z.object({ sentence: z.string() });

/** The customer-facing sentence stored on a component, or empty when the JSON is not a rationale. */
export function reasonSentence(rationaleJson: string): string {
  try {
    const parsed: unknown = JSON.parse(rationaleJson);
    const fit = StoredRationale.safeParse(parsed);

    return fit.success ? fit.data.sentence : "";
  } catch {
    return "";
  }
}

const Flavor = z.enum(["vanilla", "chocolate"]);

const Origin = z.enum(["engine", "physician", "customer_removal", "practitioner", "preference"]);

const Status = z.enum(["draft", "in_review", "approved", "declined", "superseded"]);

const Ingredient = z.object({
  ingredientId: z.string(),
  name: z.string(),
  dose: z.number(),
  unit: z.string(),
  reason: z.string(),
  addon: z.boolean(),
});

const Formula = z.object({
  id: z.string(),
  version: z.number().int(),
  status: Status,
  origin: Origin,
  proteinBase: z.string().nullable(),
  flavor: Flavor.nullable(),
  physicianName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  createdAt: z.string(),
  ingredients: z.array(Ingredient),
});

const Change = z.object({
  ingredientId: z.string(),
  name: z.string(),
  kind: z.enum(["added", "removed", "dose_changed"]),
  fromDose: z.number().nullable(),
  toDose: z.number().nullable(),
  unit: z.string(),
});

const Version = z.object({
  id: z.string(),
  version: z.number().int(),
  status: Status,
  origin: Origin,
  why: z.string(),
  createdAt: z.string(),
  proteinBase: z.string().nullable(),
  flavor: Flavor.nullable(),
  changes: z.array(Change),
});

const Home = z.object({
  current: Formula.nullable(),
  versions: z.array(Version),
});

/** The account home payload from a JSON body, or null when it is not one. */
export function readAccountFormulas(text: string): AccountFormulas | null {
  try {
    const parsed = Home.safeParse(JSON.parse(text));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
